/* eslint-disable no-shadow,no-param-reassign,no-mixed-operators */
import router from '../../router/index';
import userService from '../../api/user-service';
import authService from '../../api/auth-service';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '@/util/local-storage';
import { getDefaultUserFilterParams } from '@/store/modules/user-module';
import * as Sentry from '@sentry/vue';

const state = {
  accessToken: null,
  impersonatingAs: null,
  currentUser: null,
  lastAccessedRoute: null,
  refreshTokenPromise: null,
  shouldImportUsers: false,
  typeToImport: null,
  mustChangePassword: false,
  mustCompleteProfile: false,
};

const getters = {
  currentUser(state) {
    return state.currentUser;
  },

  accessToken(state) {
    return state.accessToken;
  },

  impersonatingAs(state) {
    return state.impersonatingAs;
  },
  permissions(state) {
    const permissionMap = {};
    for (let i = 0; i < state.currentUser?.permissions.length; i++) {
      permissionMap[state.currentUser.permissions[i].permission] = true;
    }
    return permissionMap;
  },
};

const mutations = {
  SET_ACCESS_TOKEN(state, accessToken) {
    state.accessToken = accessToken;
  },

  SET_CURRENT_USER(state, currentUser) {
    state.currentUser = currentUser;
  },

  SET_LAST_ACCESSED_ROUTE(state, route) {
    state.lastAccessedRoute = route;
  },

  SET_REFRESH_TOKEN_PROMISE(state, promise) {
    state.refreshTokenPromise = promise;
  },

  CLEAR_AUTH_DATA(state) {
    state.accessToken = null;
    state.currentUser = null;
    state.permissions = null;
    state.impersonatingAs = null;
    state.shouldImportUsers = false;
    state.mustChangePassword = false;
  },

  SET_IMPERSONATED_USER_ID(state, id) {
    state.impersonatingAs = id;
  },

  UPDATE_SHOULD_IMPORT_USERS(state, shouldImport) {
    state.shouldImportUsers = shouldImport;
  },

  UPDATE_TYPE_TO_IMPORT(state, type) {
    state.typeToImport = type;
  },

  UPDATE_MUST_CHANGE_PASSWORD(state, mustChange) {
    state.mustChangePassword = mustChange;
  },

  UPDATE_MUST_COMPLETE_PROFILE(state, mustChange) {
    state.mustCompleteProfile = mustChange;
  }
};

const actions = {
  handleAuthData({ commit }, payload) {
    commit('SET_ACCESS_TOKEN', payload.access_token);
    setLocalStorageItem('accessToken', payload.access_token);
    setLocalStorageItem('refreshToken', payload.refresh_token);
  },

  async refreshToken({ dispatch }, refreshToken) {
    try {
      const response = await authService.refreshToken(refreshToken);
      dispatch('handleAuthData', response.data);
    } catch (e) {
      dispatch('logout');
    }
  },

  async tryAutoLogin({ commit, dispatch }) {
    if (state.accessToken && state.currentUser) {
      // user is already logged in
      return;
    }

    const accessToken = getLocalStorageItem('accessToken');
    if (accessToken) {
      commit('SET_ACCESS_TOKEN', accessToken);
    } else {
      return;
    }

    const impersonatedUserId = getLocalStorageItem('impersonatedUserId');
    if (+impersonatedUserId > 0) {
      commit('SET_IMPERSONATED_USER_ID', +impersonatedUserId);
    }

    const requests = [dispatch('getCurrentUser'), dispatch('checkShouldImportUsers')];
    await Promise.allSettled(requests);
  },

  checkShouldImportUsers({ commit }) {
    return authService.checkForLastUsersImport().then((res) => {
      if (res.data.need_update) {
        commit('UPDATE_SHOULD_IMPORT_USERS', res.data.need_update);
      }
      if (res.data.type) {
        commit('UPDATE_TYPE_TO_IMPORT', res.data.type);
      }
    });
  },

  logout({ commit, dispatch }) {
    commit('CLEAR_AUTH_DATA');
    removeLocalStorageItem('accessToken');
    removeLocalStorageItem('refreshToken');
    removeLocalStorageItem('impersonatedUserId');
    dispatch('expiration/stopCheckingTokenExpiration', null, { root: true });

    if (router.currentRoute.name !== 'login') {
      router.replace({ name: 'login' });
    }
  },

  async getCurrentUser({ commit, state, dispatch }) {
    let response;
    if (!state.accessToken) {
      return response;
    }
    try {
      response = await userService.getCurrent();
      dispatch('expiration/checkTokenExpiration', null, { root: true });
      commit('SET_CURRENT_USER', response.data);
      Sentry.setUser({ id: response.data.id });
    } catch (e) {
      if (e?.response?.status === 429) {
        return;
      }
      response = e;

      if (state.accessToken && state.currentUser) {
        dispatch('logout');
      }
    }
    return response;
  },

  async impersonateUser(context, user) {
    setLocalStorageItem('impersonatedUserId', user.id);
    const profilePageUrl = router.resolve({ name: 'profile' }).href;
    window.location.replace(profilePageUrl);
  },

  stopImpersonating() {
    removeLocalStorageItem('impersonatedUserId');
    const usersPageUrl = router.resolve({
      name: 'users',
      query: getDefaultUserFilterParams(),
    }).href;
    window.location.replace(usersPageUrl);
  },
};

const authModule = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default authModule;
